import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit3, Save, X } from "lucide-react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

function renderMarkdown(text) {
    if (!text) return null;
    // Simple markdown-to-HTML: headers, bold, italic, code blocks, inline code, lists, links, paragraphs
    const html = text
        // Code blocks (``` ... ```)
        .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
        // Headers
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        // Bold and italic
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        // Inline code
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        // Links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        // Unordered lists
        .replace(/^- (.+)$/gm, '<li>$1</li>')
        .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
        // Line breaks to paragraphs (double newline)
        .replace(/\n\n/g, '</p><p>')
        // Single line breaks within paragraphs
        .replace(/\n/g, '<br/>');

    return <div className="help-content" dangerouslySetInnerHTML={{ __html: '<p>' + html + '</p>' }} />;
}

function HelpPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [editContent, setEditContent] = useState("");
    const [saving, setSaving] = useState(false);

    const canEdit = hasPermission("admin.help");

    const loadDoc = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.helpDoc(slug);
            setDoc(data);
            setEditContent(data.content || "");
        } catch (err) {
            if (err.message.includes("403")) {
                setError("access_denied");
            } else if (err.message.includes("404")) {
                setError("not_found");
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => { loadDoc(); }, [loadDoc]);

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.updateHelpDoc(slug, { content: editContent });
            setDoc(prev => ({ ...prev, content: editContent }));
            setEditing(false);
        } catch (err) {
            alert("Failed to save: " + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="help-page">
                <div className="loading">Loading documentation...</div>
            </div>
        );
    }

    if (error === "access_denied") {
        return (
            <div className="help-page">
                <button className="help-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={16} /> Back
                </button>
                <div className="empty-state">
                    <h3>Access Denied</h3>
                    <p>You do not have permission to view this documentation.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="help-page">
                <button className="help-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={16} /> Back
                </button>
                <div className="empty-state">
                    <h3>Error</h3>
                    <p>{error === "not_found" ? "Documentation page not found." : error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="help-page">
            <div className="help-page-header">
                <button className="help-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={16} /> Back
                </button>
                {canEdit && !editing && (
                    <button className="help-edit-btn" onClick={() => setEditing(true)}>
                        <Edit3 size={14} /> Edit
                    </button>
                )}
            </div>

            <h1 className="help-page-title">{doc.title}</h1>

            {doc.updatedBy && (
                <p className="help-page-meta">
                    Last updated by {doc.updatedBy}
                    {doc.updatedAt && (" on " + new Date(doc.updatedAt).toLocaleDateString())}
                </p>
            )}

            {editing ? (
                <div className="help-edit-area">
                    <textarea
                        value={editContent}
                        onChange={e => setEditContent(e.target.value)}
                        placeholder="Write documentation using markdown..."
                        rows={20}
                    />
                    <div className="help-edit-actions">
                        <button className="action-submit" onClick={handleSave} disabled={saving}>
                            <Save size={14} /> {saving ? "Saving..." : "Save"}
                        </button>
                        <button className="action-cancel" onClick={() => { setEditing(false); setEditContent(doc.content || ""); }}>
                            <X size={14} /> Cancel
                        </button>
                    </div>
                </div>
            ) : doc.content ? (
                renderMarkdown(doc.content)
            ) : (
                <div className="empty-state">
                    <h3>No documentation yet</h3>
                    <p>{canEdit ? "Click Edit to add documentation for this page." : "Documentation has not been added for this page yet."}</p>
                </div>
            )}
        </div>
    );
}

export default HelpPage;
